/**
 * useQuotaSSEListener -- Handles quota-related SSE events.
 *
 * Listens for quota_warning, quota_exceeded, pool_exhausted and budget_exceeded events from the stream:
 *   - quota_warning: invalidates the quota query to get fresh data
 *   - quota_exceeded: sets the exceeded banner descriptor + invalidates query
 *   - pool_exhausted (FEAT-036): sets the pool banner descriptor (pool name + retry seconds)
 *     + invalidates query
 *
 * The banner is an i18n DESCRIPTOR (messageId + values), never a fixed text: the component
 * formats it with the active locale.
 *
 * applyQuotaSSEEvent is the pure core, shared with streamManager (which has no React context).
 * The hook is only a React wrapper.
 *
 * Usage: call handleQuotaEvent(event) from the SSE onEvent callback.
 */

import { useCallback, useRef } from 'react';
import type { SSEEvent } from '@/types/stream.types';
import { useQuotaStore, type ExceededBannerDescriptor, type GraceNotice } from '@/stores/quotaStore';
import { parseQuotaExceededInfo, quotaExceededDescriptor } from '@/utils/quotaExceededText';
import { useMyQuotas } from './useMyQuotas';

export interface QuotaSSEActions {
  invalidate: () => void;
  setBanner: (banner: ExceededBannerDescriptor) => void;
  /** FEAT-054: grace entered (optional so older callers keep working) */
  notifyGrace?: (notice: GraceNotice) => void;
}

/** Pure: maps one SSE event to quota actions; non-quota events are ignored. */
export function applyQuotaSSEEvent(event: SSEEvent, actions: QuotaSSEActions): void {
  switch (event.type) {
    case 'quota_warning':
      // FEAT-054: entering the grace margin also raises a one-time toast (deduplicated by the consumer)
      if (event.data.level === 'grace') {
        const remaining = event.data.remaining_tokens;
        actions.notifyGrace?.(typeof remaining === 'number' ? { remaining } : {});
      }
      // Immediately refetch to get updated numbers
      actions.invalidate();
      break;

    case 'quota_exceeded': {
      // FEAT-054: with grace_mode the banner states mode and percentage (same text as the 429);
      // without it (gateway path / older server) the legacy text stays.
      const info = parseQuotaExceededInfo({
        graceMode: event.data.grace_mode,
        gracePct: event.data.grace_pct,
        resetAt: event.data.reset_at,
      });
      actions.setBanner(info ? quotaExceededDescriptor(info) : { messageId: 'quota.exceededBanner' });
      actions.invalidate();
      break;
    }

    case 'pool_exhausted': {
      // FEAT-037: the gateway block notice does not always identify the pool or the deadline;
      // then a generic localized text is used (never null, id or raw number).
      const { pool_name: pool, retry_after_seconds: seconds } = event.data;
      actions.setBanner(
        pool && typeof seconds === 'number'
          ? { messageId: 'quota.pool.exhaustedBanner', values: { pool, seconds } }
          : { messageId: 'quota.pool.exhaustedBannerGeneric' },
      );
      actions.invalidate();
      break;
    }

    case 'budget_exceeded': {
      // FEAT-038: only a blocking budget produces the banner (warn-only never reaches the stream).
      if (event.data.action !== 'block') break;
      const resetMs = Date.parse(event.data.reset_at);
      // The date is formatted by the ICU message itself in the browser time zone; an unreadable
      // reset_at uses the generic text (never an ISO string or "Invalid Date").
      actions.setBanner(
        Number.isNaN(resetMs)
          ? { messageId: 'chat.budget.exceededGeneric' }
          : {
              messageId: event.data.contact_gestor ? 'chat.budget.exceeded' : 'chat.budget.exceededNoGestor',
              values: { date: resetMs },
            },
      );
      actions.invalidate();
      break;
    }

    default:
      // Not a quota event -- ignore
      break;
  }
}

// --- E2E-FINAL-FIX (UI-1, 13/10/2026) ---
/**
 * True when the event ENDS the turn: the server cuts the generation right after quota_exceeded / pool_exhausted and
 * after a budget_exceeded with action 'block' (legacy HARD_CUT and gateway hard block, GatewaySurfaceNotifier). The Chat
 * tab must then drop its "generating" state; before, "Escrevendo resposta..." and the Stop button stayed for 20 s or
 * more after the server had already finalized the turn as cancelled.
 */
export function isTurnEndingQuotaEvent(event: SSEEvent): boolean {
  if (event.type === 'quota_exceeded' || event.type === 'pool_exhausted') return true;
  return event.type === 'budget_exceeded' && event.data.action === 'block';
}
// --- fim E2E-FINAL-FIX ---

export interface UseQuotaSSEListenerReturn {
  /** Call this with every SSE event; it filters for quota events internally */
  handleQuotaEvent: (event: SSEEvent) => void;
}

export function useQuotaSSEListener(): UseQuotaSSEListenerReturn {
  const { invalidateQuotas } = useMyQuotas();
  const setExceededBanner = useQuotaStore((s) => s.setExceededBanner);

  // Refs to avoid stale closures
  const invalidateRef = useRef(invalidateQuotas);
  invalidateRef.current = invalidateQuotas;
  const setBannerRef = useRef(setExceededBanner);
  setBannerRef.current = setExceededBanner;
  const setGraceNotice = useQuotaStore((s) => s.setGraceNotice);
  const graceRef = useRef(setGraceNotice);
  graceRef.current = setGraceNotice;

  const handleQuotaEvent = useCallback((event: SSEEvent) => {
    applyQuotaSSEEvent(event, {
      invalidate: () => invalidateRef.current(),
      setBanner: (banner) => setBannerRef.current(banner),
      notifyGrace: (notice) => graceRef.current(notice),
    });
  }, []);

  return { handleQuotaEvent };
}
